import { useWinStore } from "../../windows/windowStore";
import { WhatsappCompose, WHATSTAPP_STATUS } from "../WhatsappCompose";
import { PanelWhatsAppTitle } from "../PanelWhatsAppTitle";
import { OpenWhatsappPanelParams, WhatsappPanelMode } from "../typings";
import ClientService from "../../../services/ClientService";
import CompanyService from "../../../services/CompanyService";

export async function openWhatsappWindow({
  client: clientArg,
  personId,
  onSubmit,
  afterFinally,
  historyMessageId,
  initialMessage,
  onMessageSent
}: OpenWhatsappPanelParams): Promise<string | null> {
  let client = clientArg;

  try {
    if (!client) {
      const id = personId?.toString();
      if (!id) return null;
      const res = await ClientService.get({ id });
      if (res instanceof Error) return null;
      client = res.item;
      if (!client) return null;
    }

    const mode: WhatsappPanelMode = historyMessageId
      ? { kind: "history", messageId: historyMessageId.toString() }
      : { kind: "active", personId: client.PersonId.toString() };

    const whatsappPhone =
      CompanyService.getCompanyAuth()?.DefaultWhatsappField === -1
        ? client.Phone
        : client.additionals.find(
            (x) =>
              x.id === CompanyService.getCompanyAuth()!.DefaultWhatsappField
          )?.value;

    // Crear identificador único para evitar duplicados
    const identifier = `whatsapp-${mode.kind}-${mode.kind === "history" ? mode.messageId : mode.personId}`;

    const { open, windows, focus } = useWinStore.getState();

    // Verificar si ya existe una ventana para este chat
    const existingWindow = windows.find((w) => w.identifier === identifier);
    if (existingWindow) {
      // Si existe, solo enfocarla
      focus(existingWindow.id);
      return existingWindow.id;
    }

    // Buscar la última ventana de WhatsApp abierta (no minimizada) para posicionar la nueva
    const whatsappWindows = windows.filter(
      (w) => w.identifier?.startsWith("whatsapp-") && !w.minimized
    );
    const lastWhatsappWindow =
      whatsappWindows.length > 0
        ? whatsappWindows.reduce((prev, current) =>
            current.z > prev.z ? current : prev
          )
        : null;

    // Calcular posición X: 20px a la izquierda de la última ventana de WhatsApp
    let x: number | undefined = undefined;
    if (lastWhatsappWindow) {
      const lastX =
        typeof lastWhatsappWindow.width === "number"
          ? lastWhatsappWindow.x
          : Number(lastWhatsappWindow.x);
      x = Math.max(0, lastX - 20); // 20px a la izquierda, pero no menos de 0
    }

    let headerApi: {
      setHideActivityTypeSelector: (hidden: boolean) => void;
      setPhone: (phone: string | undefined) => void;
      setPhoneLoading: (loading: boolean) => void;
      setCanAssignCollector: (can: boolean) => void;
      reloadCollector: () => void;
      setMessageId: (messageId: string | number | undefined) => void;
    } | null = null;
    let setActivityTypeFromTitle: ((activityTypeId: number) => void) | null =
      null;
    let clearActivityTypeFromTitle: (() => void) | null = null;

    const windowId = open({
      title: (
        <PanelWhatsAppTitle
          name={client?.Name}
          phone={whatsappPhone}
          messageId={historyMessageId}
          onSelectTemplate={(activityTypeId) => {
            setActivityTypeFromTitle?.(activityTypeId);
          }}
          registerExternalController={(api) => {
            headerApi = api;
          }}
        />
      ),
      identifier,
      metadata: {
        name: client?.Name,
        phone: whatsappPhone
      },
      width: 450,
      height: 650,
      x, // Posición X calculada (20px a la izquierda de la anterior)
      placement: "right-cascade",
      content: (
        <div
          style={{ height: "100%", display: "flex", flexDirection: "column" }}
        >
          <div style={{ flex: 1, overflow: "hidden" }}>
            <WhatsappCompose
              person={client!}
              mode={mode}
              initialMessage={initialMessage}
              onMessageSent={onMessageSent}
              onSubmit={() => {
                headerApi.reloadCollector();
                onSubmit && onSubmit();
              }}
              onChangeStatus={(status: WHATSTAPP_STATUS) =>
                headerApi.setCanAssignCollector(
                  status === WHATSTAPP_STATUS.DERIVATED ||
                    status === WHATSTAPP_STATUS.OPEN
                )
              }
              onMainMessageState={(hasMain, mainMessageId) => {
                headerApi?.setHideActivityTypeSelector(hasMain);
                // En modo "active" (detalle del cliente) no llega historyMessageId,
                // el header necesita el id del mensaje principal para el "Visto el"
                if (!historyMessageId) headerApi?.setMessageId(mainMessageId);
              }}
              onRegisterActivityTypeSetter={(setFn, clearFn) => {
                setActivityTypeFromTitle = setFn;
                clearActivityTypeFromTitle =
                  clearFn || clearActivityTypeFromTitle;
              }}
              onRegisterPhoneSetter={(setHeaderPhoneFn) => {
                // Conectar el setter con el header
                // setHeaderPhoneFn recibe una función que guarda el setter del header
                const updateHeaderPhone = (phone: string | undefined) => {
                  headerApi?.setPhone(phone);
                };
                // Pasamos el setter directamente a WhatsappCompose
                setHeaderPhoneFn(updateHeaderPhone as any);
              }}
              onRegisterPhoneLoadingSetter={(setHeaderPhoneLoadingFn) => {
                // Conectar el setter de loading con el header
                const updateHeaderPhoneLoading = (loading: boolean) => {
                  headerApi?.setPhoneLoading(loading);
                };
                // Pasamos el setter directamente a WhatsappCompose
                setHeaderPhoneLoadingFn(updateHeaderPhoneLoading as any);
              }}
              whatsappPhone={whatsappPhone}
            />
          </div>
        </div>
      )
    });

    return windowId;
  } finally {
    afterFinally?.();
  }
}

// Hook de utilidad para gestionar ventanas de WhatsApp
export const useWhatsappWindows = () => {
  const windows = useWinStore((s) => s.windows);
  const whatsappWindows = windows.filter((w) =>
    w.identifier?.startsWith("whatsapp-")
  );

  return {
    whatsappWindows,
    activeChats: whatsappWindows.filter((w) =>
      w.identifier?.includes("-active-")
    ),
    historyChats: whatsappWindows.filter((w) =>
      w.identifier?.includes("-history-")
    ),
    count: whatsappWindows.length
  };
};
